import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Input } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Label } from "../ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { EmptyState } from "../shared/EmptyState";
import { ErrorState } from "../shared/ErrorState";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { todayLocal } from "../../lib/utils";
import { useListState } from "../../hooks/useListState";

export function DailyDeliveryTab() {
  const [date, setDate] = useListState("reports.dailyDelivery.date", todayLocal());
  const [type, setType] = useListState("reports.dailyDelivery.type", "2");
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = () => {
    setState({ loading: true, error: null, data: null });
    apiClient.get("/reports/daily-delivery", { params: { date, type } })
      .then(({ data }) => setState({ loading: false, error: null, data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load the daily delivery report."), data: null }));
  };

  useEffect(load, [date, type]);

  const r = state.data;

  return (
    <div className="mt-4 space-y-4" data-testid="daily-delivery-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="daily-delivery-date">Date</Label>
          <Input id="daily-delivery-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-delivery-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="daily-delivery-type">Type</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger id="daily-delivery-type" className="w-40" data-testid="daily-delivery-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2">Delivery</SelectItem>
              <SelectItem value="1">Pickup</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {state.error ? (
        <ErrorState message={state.error} onRetry={load} testId="daily-delivery-error" />
      ) : state.loading ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Card data-testid="daily-delivery-eligible-count-card"><CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Eligible Today</p>
              <p className="mt-1 font-heading text-2xl font-bold text-text-main">{r.eligible_count}</p>
            </CardContent></Card>
            <Card data-testid="daily-delivery-skipped-count-card"><CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Skipped (One-off)</p>
              <p className="mt-1 font-heading text-2xl font-bold text-text-main">{r.skipped_deliveries_count}</p>
            </CardContent></Card>
            <Card data-testid="daily-delivery-skipped-day-count-card"><CardContent className="p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Skipped (Weekly Day Off)</p>
              <p className="mt-1 font-heading text-2xl font-bold text-text-main">{r.skipped_sub_days_count}</p>
            </CardContent></Card>
          </div>

          <Card data-testid="daily-delivery-eligible-table-card">
            <CardContent className="p-0">
              {r.eligible.length === 0 ? (
                <EmptyState title="No eligible subscriptions" description="Nobody is scheduled for this date/type." testId="daily-delivery-empty" />
              ) : (
                <Table data-testid="daily-delivery-eligible-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Customer</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Qty</TableHead>
                      <TableHead>Delivered So Far</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead>Phone</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {r.eligible.map((e) => (
                      <TableRow key={e.subscription_id} data-testid={`daily-delivery-row-${e.subscription_id}`}>
                        <TableCell>{e.customer_name || `Customer #${e.customer_id}`}</TableCell>
                        <TableCell>{e.plan_name || "—"}</TableCell>
                        <TableCell>{e.plan_quantity ?? "—"}</TableCell>
                        <TableCell>{e.deliveries_done ?? "—"}</TableCell>
                        <TableCell className="max-w-xs truncate">{e.address || "—"}</TableCell>
                        <TableCell>{e.phone || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {(r.skipped_deliveries.length > 0 || r.skipped_sub_days.length > 0) && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {r.skipped_deliveries.length > 0 && (
                <Card data-testid="daily-delivery-skipped-table-card">
                  <CardContent className="p-4">
                    <p className="mb-2 font-heading text-sm font-semibold text-text-main">Skipped for this date</p>
                    <ul className="space-y-1.5 text-sm text-text-muted">
                      {r.skipped_deliveries.map((s, i) => (
                        <li key={i} data-testid={`daily-delivery-skipped-row-${i}`}>{s.customer_name || `Sub #${s.subscription_id}`}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
              {r.skipped_sub_days.length > 0 && (
                <Card data-testid="daily-delivery-skipped-day-table-card">
                  <CardContent className="p-4">
                    <p className="mb-2 font-heading text-sm font-semibold text-text-main">Weekly day-off for {r.weekday}</p>
                    <ul className="space-y-1.5 text-sm text-text-muted">
                      {r.skipped_sub_days.map((s, i) => (
                        <li key={i} data-testid={`daily-delivery-skipped-day-row-${i}`}>{s.customer_name || `Sub #${s.subscription_id}`} — {s.plan_name}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
